import React from "react";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import Partner from "../components/Partner";
import Consultation from "../components/Consultation";

const Home = () => {
  const featuredServices = [
    {
      title: "Management Consulting",
      description: "Organizational assessment, restructuring, workflow optimization, and leadership governance for scaling enterprises.",
      icon: "🏢"
    },
    {
      title: "Market Research & Intelligence",
      description: "Feasibility studies, competitor benchmarking reports, and pricing analyses for data-backed expansion decisions.",
      icon: "📊"
    },
    {
      title: "Business Strategy & Expansion",
      description: "Scalable growth frameworks, revenue model design, and strategic cross-border expansion tactics.",
      icon: "🚀"
    },
    {
      title: "Marketing Consultancy",
      description: "High-impact brand positioning, omnichannel campaign planning, and value proposition refinement.",
      icon: "📈"
    }
  ];

  const targetIndustries = [
    {
      title: "General Trading",
      image: "/img/Home-1.avif",
      description: "Global trade compliance, import-export structures, and cross-border supply chain logistics."
    },
    {
      title: "Commodities Market",
      image: "/img/home-2.avif",
      description: "Supply and demand market intelligence, price volatility modeling, and trade flow hedging."
    },
    {
      title: "Raw Materials",
      image: "img/home-3.avif",
      description: "Strategic procurement, sustainable resource sourcing, and upstream value chain integration."
    },
    {
      title: "Industrial Businesses",
      image: "img/home-4.avif",
      description: "Plant performance optimization, operational restructuring, and lean manufacturing guidance."
    }
  ];

  return (
    <>
      <Hero />
      <Partner />

      <section className="py-24 px-6 bg-[#faeee2]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16" data-aos="fade-up">
            <span className="inline-block px-5 py-1.5 rounded-full bg-[#f2decb] text-brand-darkBrown text-xs md:text-sm font-bold tracking-widest uppercase mb-4 border border-[#a17644]/30">
              OUR CORE PRACTICES
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-brand-deepBrown mb-6">
              Expertise Driven By <span className="text-brand-orange">Market Intelligence</span>
            </h2>
            <p className="text-brand-grayBrown text-base md:text-lg leading-relaxed">
              Capilano equips leaders with empirical data and practical execution blueprints to navigate complex commercial landscapes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredServices.map((service, idx) => (
              <div
                key={idx}
                className="bg-white p-8 rounded-3xl border border-[#a17644]/20 shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between"
                data-aos="fade-up"
                data-aos-delay={idx * 100}
              >
                <div>
                  <div className="text-4xl mb-6">{service.icon}</div>
                  <h3 className="text-xl font-serif font-bold text-brand-deepBrown mb-3">
                    {service.title}
                  </h3>
                  <p className="text-brand-grayBrown text-sm leading-relaxed mb-6">
                    {service.description}
                  </p>
                </div>
                <Link
                  to="/services"
                  className="text-xs font-bold text-brand-orange uppercase tracking-wider hover:text-brand-darkBrown transition-colors inline-flex items-center gap-1"
                >
                  Explore Practice <span className="text-lg leading-none">&rarr;</span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#f2decb]/50 border-y border-[#a17644]/20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div data-aos="fade-right">
              <span className="text-xs font-bold text-brand-orange uppercase tracking-widest block mb-3">
                WHY LEADERS CHOOSE US
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-brand-deepBrown mb-6 leading-tight">
                Transforming Complexity Into Actionable Strategic Clarity
              </h2>
              <p className="text-brand-grayBrown text-base md:text-lg leading-relaxed mb-8">
                We combine deep sector knowledge in international trading, commodities, and industrial operations with uncompromised research discipline.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-brand-orange text-white flex items-center justify-center font-bold shrink-0 mt-1">
                    01
                  </div>
                  <div>
                    <h4 className="font-serif font-bold text-brand-deepBrown text-lg mb-1">
                      Empirical Data Foundations
                    </h4>
                    <p className="text-brand-grayBrown text-sm leading-relaxed">
                      Our recommendations rely on raw market intelligence, feasibility audits, and benchmark data rather than surface assumptions.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-brand-orange text-white flex items-center justify-center font-bold shrink-0 mt-1">
                    02
                  </div>
                  <div>
                    <h4 className="font-serif font-bold text-brand-deepBrown text-lg mb-1">
                      End-To-End Execution Blueprints
                    </h4>
                    <p className="text-brand-grayBrown text-sm leading-relaxed">
                      We design precise implementation roadmaps with governance frameworks and clear performance KPIs.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-brand-orange text-white flex items-center justify-center font-bold shrink-0 mt-1">
                    03
                  </div>
                  <div>
                    <h4 className="font-serif font-bold text-brand-deepBrown text-lg mb-1">
                      Targeted Sector Specialization
                    </h4>
                    <p className="text-brand-grayBrown text-sm leading-relaxed">
                      Deep domain advisory across general trading, raw material supply chains, commodities, and manufacturing.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div data-aos="fade-left" className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-[#f2decb]">
                <img
                  src="/img/About-img.avif"
                  alt="Strategic Advisory Session"
                  className="w-full h-[520px] object-cover"
                  onError={(e) => {
                    e.target.src = "/img/img-6.avif";
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 bg-[#faeee2]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6" data-aos="fade-up">
            <div className="max-w-2xl">
              <span className="text-xs font-bold text-brand-orange uppercase tracking-widest block mb-3">
                SECTORS WE SERVE
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-brand-deepBrown mb-4">
                Tailored Industry Solutions
              </h2>
              <p className="text-brand-grayBrown text-base md:text-lg">
                Specialized market research and strategic guidance for high-impact commercial domains.
              </p>
            </div>
            <Link
              to="/industries"
              className="inline-flex px-8 py-3.5 bg-brand-darkBrown text-white font-bold rounded-full hover:bg-brand-orange transition-all duration-300 uppercase tracking-wider text-xs whitespace-nowrap shadow-md"
            >
              View All Sectors
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {targetIndustries.map((ind, idx) => (
              <div
                key={idx}
                className="bg-white rounded-3xl overflow-hidden border border-[#a17644]/20 shadow-md hover:shadow-2xl transition-all duration-300 flex flex-col justify-between"
                data-aos="fade-up"
                data-aos-delay={idx * 100}
              >
                <div>
                  <div className="h-48 overflow-hidden bg-[#f2decb]">
                    <img
                      src={ind.image}
                      alt={ind.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = "/img/home-1.avif";
                      }}
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-serif font-bold text-brand-deepBrown mb-3">
                      {ind.title}
                    </h3>
                    <p className="text-brand-grayBrown text-xs md:text-sm leading-relaxed mb-4">
                      {ind.description}
                    </p>
                  </div>
                </div>
                <div className="p-6 pt-0">
                  <Link
                    to="/industries"
                    className="text-xs font-bold text-brand-orange uppercase tracking-wider hover:text-brand-darkBrown transition-colors inline-flex items-center gap-1"
                  >
                    View Frameworks <span className="text-lg leading-none">&rarr;</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Consultation />
    </>
  );
};

export default Home;